import React, { useEffect, useState } from 'react';
import { lire } from './annoncesApi';

/**
 * Les critères éliminatoires d'une annonce. Le REGISTRE vient du serveur
 * (GET /annonces/criteres : types, situations de travail, niveaux, langues,
 * délais) — l'écran n'en code aucune liste. Chaque critère : ses réglages,
 * « éliminatoire » (sauf la question ouverte, qui ne l'est jamais), retirer.
 * La question qu'on montrera au candidat est servie par l'annonce enregistrée.
 */
const BI = ({ icon }) => <i className={`bi bi-${icon}`} aria-hidden="true" />;

// Le point de départ d'une offre : l'autorisation de travail en Suisse.
export const CRITERES_DEPART = [{ type: 'autorisation_travail', params: { acceptes: ['suisse', 'ue_aele', 'permis_c', 'permis_b', 'permis_g', 'permis_l'] }, eliminatoire: true }]; // permis L coché d'office (14.10)
// Ce que le serveur attend : ni libellé ni question (c'est lui qui les sert).
export const nettoyerCriteres = (crits) => (crits || []).map(({ id, type, params, eliminatoire }) => ({ ...(id ? { id } : {}), type, params, eliminatoire }));
// Le registre (GET /annonces/criteres), lu une fois, quand on en a besoin.
// Route absente : `absent`, et l'écran publie sans proposer de critères.
export function useRegistreCriteres(actif = true) {
  const [registre, setRegistre] = useState(null);
  const [absent, setAbsent] = useState(false);
  useEffect(() => {
    if (!actif || registre || absent) return undefined;
    let vivant = true;
    lire('/annonces/criteres').then(r => { if (!vivant) return; if (r.absent) setAbsent(true); else if (!r.error) setRegistre(r); });
    return () => { vivant = false; };
  }, [actif, registre, absent]);
  return { registre, absent };
}

function Reglages({ c, registre, maj }) {
  const p = c.params || {};
  const set = (k, v) => maj({ ...c, params: { ...p, [k]: v } });
  switch (c.type) {
    case 'autorisation_travail':
      return (
        <fieldset className="ann-crit__champs">
          <legend className="ann-crit__legende">Situations acceptées</legend>
          <div className="ann-crit__cases">
            {(registre.permis || []).map(x => (
              <label key={x.cle} className="ann-case">
                <input type="checkbox" checked={(p.acceptes || []).includes(x.cle)}
                  onChange={e => set('acceptes', e.target.checked ? [...(p.acceptes || []), x.cle] : (p.acceptes || []).filter(k => k !== x.cle))} />
                {x.libelle}
              </label>
            ))}
          </div>
        </fieldset>
      );
    case 'langue':
      return (
        <div className="ann-crit__champs ann-crit__champs--ligne">
          <label className="ann-champ"><span>Langue</span>
            <select className="erp-field" value={p.langue || ''} onChange={e => set('langue', e.target.value)}>
              {(registre.langues || []).map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </label>
          <label className="ann-champ"><span>Niveau minimum</span>
            <select className="erp-field" value={p.niveau_min || ''} onChange={e => set('niveau_min', e.target.value)}>
              {(registre.niveaux || []).map(n => <option key={n.cle} value={n.cle}>{n.libelle}</option>)}
            </select>
          </label>
        </div>
      );
    case 'disponibilite':
      return (
        <label className="ann-champ"><span>Commencer au plus tard</span>
          <select className="erp-field" value={p.delai_max || ''} onChange={e => set('delai_max', e.target.value)}>
            {(registre.delais || []).map(d => <option key={d.cle} value={d.cle}>{d.libelle}</option>)}
          </select>
        </label>
      );
    case 'experience':
      return (
        <div className="ann-crit__champs ann-crit__champs--ligne">
          <label className="ann-champ ann-champ--court"><span>Années minimum</span>
            <input className="erp-field" type="number" min="1" max="30" value={p.annees_min ?? ''} onChange={e => set('annees_min', e.target.value === '' ? '' : Number(e.target.value))} />
          </label>
          <label className="ann-champ"><span>Dans quel domaine <em>facultatif</em></span>
            <input className="erp-field" value={p.domaine || ''} placeholder="Java, gestion de projet…" onChange={e => set('domaine', e.target.value)} />
          </label>
        </div>
      );
    case 'lieu':
      return (
        <div className="ann-crit__champs ann-crit__champs--ligne">
          <label className="ann-champ"><span>Ville</span>
            <input className="erp-field" value={p.lieu || ''} onChange={e => set('lieu', e.target.value)} />
          </label>
          <label className="ann-champ"><span>Précision <em>facultatif</em></span>
            <input className="erp-field" value={p.precision || ''} placeholder="2 jours de télétravail" onChange={e => set('precision', e.target.value)} />
          </label>
        </div>
      );
    case 'oui_non':
      return (
        <div className="ann-crit__champs ann-crit__champs--ligne">
          <label className="ann-champ ann-champ--large"><span>Question</span>
            <input className="erp-field" value={p.question || ''} placeholder="Avez-vous une voiture ?" onChange={e => set('question', e.target.value)} />
          </label>
          <label className="ann-champ ann-champ--court"><span>Réponse attendue</span>
            <select className="erp-field" value={p.attendue || 'oui'} onChange={e => set('attendue', e.target.value)}>
              <option value="oui">Oui</option><option value="non">Non</option>
            </select>
          </label>
        </div>
      );
    case 'texte':
      return (
        <div className="ann-crit__champs">
          <label className="ann-champ"><span>Question</span>
            <input className="erp-field" value={p.question || ''} placeholder="Vos prétentions salariales ?" onChange={e => set('question', e.target.value)} />
          </label>
          <label className="ann-case"><input type="checkbox" checked={p.obligatoire === true} onChange={e => set('obligatoire', e.target.checked)} /> Réponse obligatoire</label>
        </div>
      );
    default:
      return null;
  }
}

export default function CriteresEditeur({ valeur = [], onChange, registre, lieuDefaut = '' }) {
  if (!registre) return <p className="ann-muet">Chargement des critères…</p>;
  const types = registre.criteres || [];
  const libelle = (type) => (types.find(t => t.type === type) || {}).libelle || type;
  const possible = (type) => (types.find(t => t.type === type) || {}).eliminatoire_possible !== false;
  const maj = (i, c) => onChange(valeur.map((x, j) => (j === i ? c : x)));
  const retirer = (i) => onChange(valeur.filter((_, j) => j !== i));
  const ajouter = (type) => {
    const def = types.find(t => t.type === type);
    if (!def) return;
    const params = { ...(def.defauts || {}) };
    if (type === 'lieu' && !params.lieu) params.lieu = lieuDefaut || '';
    onChange([...valeur, { type, params, eliminatoire: def.eliminatoire_possible !== false }]);
  };
  const plein = valeur.length >= (registre.max || 8);

  return (
    <div className="ann-crits">
      {valeur.length === 0 && <p className="ann-muet">Aucun critère : toute candidature complète entre. Ajoutez ce qui doit écarter un profil d'office — l'autorisation de travail, une langue…</p>}
      {valeur.map((c, i) => (
        <div className="ann-crit" key={c.id || `n${i}`}>
          <div className="ann-crit__tete">
            <strong className="ann-crit__nom">{libelle(c.type)}</strong>
            {possible(c.type)
              ? <label className="ann-case ann-case--petit"><input type="checkbox" checked={c.eliminatoire !== false} onChange={e => maj(i, { ...c, eliminatoire: e.target.checked })} /> Éliminatoire</label>
              : <span className="ann-crit__info">Pour information</span>}
            <button type="button" className="ann-icone" onClick={() => retirer(i)} aria-label={`Retirer le critère ${libelle(c.type)}`}><BI icon="x-lg" /></button>
          </div>
          <Reglages c={c} registre={registre} maj={(n) => maj(i, n)} />
          {c.question && <p className="ann-crit__question">Le candidat lit : « {c.question} »</p>}
        </div>
      ))}
      <label className="ann-champ ann-crits__ajout">
        <span className="ann-sr">Ajouter un critère</span>
        <select className="erp-field" value="" disabled={plein} onChange={e => { ajouter(e.target.value); e.target.value = ''; }}>
          <option value="">{plein ? `${registre.max || 8} critères au plus` : '+ Ajouter un critère…'}</option>
          {types.map(t => <option key={t.type} value={t.type}>{t.libelle}</option>)}
        </select>
      </label>
    </div>
  );
}
